<template>
  <div class="data-table-wrapper" :class="{ 'is-dark': isDark, 'is-light': !isDark }">
    <!-- Search Bar -->
    <div v-if="searchable" class="data-table-search">
      <input
        v-model="searchQuery"
        type="text"
        :class="['search-input', { 'has-content': searchQuery }]"
        :placeholder="searchPlaceholder || $t('datatable.search')"
      />
    </div>

    <!-- Table -->
    <div class="data-table-container" :class="{ 'is-loading': loading }">
      <!-- Loading Overlay -->
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner"></div>
      </div>

      <table class="data-table" :class="[tableClass, { 'is-striped': striped, 'is-hoverable': hoverable, 'is-loading': loading }]">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :style="column.width ? { width: column.width } : {}"
              :class="[column.class, { 'is-sortable': column.sortable !== false }]"
              @click="column.sortable !== false ? handleSort(column.field || column.key) : null"
            >
              <div class="th-content" :class="`align-${column.align || 'left'}`">
                <span>{{ column.label }}</span>
                <span v-if="column.sortable !== false" class="sort-icon">
                  <span v-if="sort.by === (column.field || column.key)">
                    {{ sort.order === 'asc' ? '▲' : '▼' }}
                  </span>
                  <span v-else class="sort-icon-inactive">⇅</span>
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody v-if="displayData.length > 0">
          <tr v-for="(row, index) in displayData" :key="index">
            <td
              v-for="column in columns"
              :key="column.key"
              :class="[column.class, `align-${column.align || 'left'}`, { 'has-truncate': column.truncate }]"
            >
              <slot :name="`cell-${column.key}`" :row="row" :value="getCellValue(row, column)" :index="pagination.startItem + index - 1">
                <div
                  v-if="column.truncate"
                  class="truncate-cell"
                  :title="column.render ? column.render(getCellValue(row, column), row, pagination.startItem + index - 1) : getCellValue(row, column)"
                  v-html="highlightText(column.render ? column.render(getCellValue(row, column), row, pagination.startItem + index - 1) : getCellValue(row, column), searchQuery)"
                />
                <span
                  v-else
                  v-html="highlightText(column.render ? column.render(getCellValue(row, column), row, pagination.startItem + index - 1) : getCellValue(row, column), searchQuery)"
                />
              </slot>
            </td>
          </tr>
        </tbody>
        <tbody v-if="!loading && displayData.length === 0">
          <tr>
            <td :colspan="columns.length" class="empty-state">
              {{ searchQuery ? (emptyMessage || $t('datatable.noResults')) : (emptyMessage || $t('datatable.noData')) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Controls -->
    <div v-if="showPagination && (serverSide ? totalItems > 0 : filteredData.length > 0)" class="data-table-footer">
      <div class="footer-left">
        <div class="items-per-page">
          <span class="label">{{ $t('datatable.itemsPerPage') }}:</span>
          <select v-model.number="pagination.itemsPerPage" class="page-size-select" @change="handlePageSizeChange">
            <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>
        <div class="showing-info">
          {{ $t('datatable.showing', { start: pagination.startItem, end: pagination.endItem, total: pagination.totalItems }) }}
        </div>
      </div>

      <div class="footer-right">
        <div class="pagination-controls">
          <button
            class="pagination-btn"
            :disabled="pagination.currentPage === 1"
            @click="goToPage(pagination.currentPage - 1)"
          >
            {{ $t('datatable.previous') }}
          </button>

          <div class="page-numbers">
            <button
              v-for="page in visiblePages"
              :key="page"
              class="page-btn"
              :class="{ active: page === pagination.currentPage }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </div>

          <button
            class="pagination-btn"
            :disabled="pagination.currentPage === pagination.totalPages"
            @click="goToPage(pagination.currentPage + 1)"
          >
            {{ $t('datatable.next') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DataTable',
  props: {
    data: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    searchable: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      default: ''
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [10, 25, 50, 100]
    },
    defaultItemsPerPage: {
      type: Number,
      default: 10
    },
    currentPage: {
      type: Number,
      default: 1
    },
    searchValue: {
      type: String,
      default: ''
    },
    defaultSortBy: {
      type: String,
      default: ''
    },
    defaultSortOrder: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    tableClass: {
      type: String,
      default: ''
    },
    striped: {
      type: Boolean,
      default: true
    },
    hoverable: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    serverSide: {
      type: Boolean,
      default: false
    },
    totalItems: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      searchQuery: this.searchValue,
      sort: {
        by: this.defaultSortBy,
        order: this.defaultSortOrder
      },
      pagination: {
        currentPage: this.currentPage,
        itemsPerPage: this.defaultItemsPerPage,
        totalItems: 0,
        totalPages: 0,
        startItem: 0,
        endItem: 0
      }
    }
  },
  computed: {
    isDark() {
      return this.$store && this.$store.state && this.$store.state.ui && this.$store.state.ui.dark
    },
    filteredData() {
      if (this.serverSide) {
        return this.data
      }

      if (!this.searchQuery) {
        return this.data
      }

      const query = this.searchQuery.toLowerCase()
      return this.data.filter(row => {
        return this.columns.some(column => {
          const value = this.getCellValue(row, column)
          return value != null && String(value).toLowerCase().includes(query)
        })
      })
    },
    sortedData() {
      if (this.serverSide) {
        return this.filteredData
      }

      if (!this.sort.by) {
        return this.filteredData
      }

      const sorted = [...this.filteredData]
      sorted.sort((a, b) => {
        const aValue = this.getNestedValue(a, this.sort.by)
        const bValue = this.getNestedValue(b, this.sort.by)

        if (aValue == null && bValue == null) return 0
        if (aValue == null) return 1
        if (bValue == null) return -1

        let comparison = 0
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue)
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue
        } else {
          comparison = String(aValue).localeCompare(String(bValue))
        }

        return this.sort.order === 'asc' ? comparison : -comparison
      })

      return sorted
    },
    displayData() {
      if (this.serverSide) {
        return this.data
      }

      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage
      const end = start + this.pagination.itemsPerPage
      return this.sortedData.slice(start, end)
    },
    visiblePages() {
      const total = this.pagination.totalPages
      const current = this.pagination.currentPage
      const delta = 2
      const pages = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        pages.push(i)
      }

      if (current - delta > 2) {
        pages.unshift('...')
      }
      if (current + delta < total - 1) {
        pages.push('...')
      }

      pages.unshift(1)
      if (total > 1) {
        pages.push(total)
      }

      return pages.filter((page, index, self) => self.indexOf(page) === index)
    }
  },
  watch: {
    sortedData: {
      handler() {
        this.updatePagination()
      },
      immediate: true
    },
    searchQuery(newValue) {
      this.pagination.currentPage = 1

      if (this.serverSide) {
        this.$emit('search', newValue)
      }
    },
    totalItems() {
      if (this.serverSide) {
        this.updatePagination()
      }
    },
    defaultSortBy(newValue) {
      if (this.sort.by !== newValue) {
        this.sort.by = newValue
      }
    },
    defaultSortOrder(newValue) {
      if (this.sort.order !== newValue) {
        this.sort.order = newValue
      }
    },
    defaultItemsPerPage(newValue) {
      if (this.pagination.itemsPerPage !== newValue) {
        this.pagination.itemsPerPage = newValue
        this.updatePagination()
      }
    },
    currentPage(newValue) {
      if (this.pagination.currentPage !== newValue) {
        this.pagination.currentPage = newValue
        this.updatePagination()
      }
    },
    searchValue(newValue) {
      if (this.searchQuery !== newValue) {
        this.searchQuery = newValue
      }
    }
  },
  methods: {
    highlightText(text, query) {
      if (!query || !text) {
        return text
      }

      const textStr = String(text)
      const queryStr = String(query)
      const regex = new RegExp(`(${queryStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      return textStr.replace(regex, '<strong>$1</strong>')
    },
    getCellValue(row, column) {
      const field = column.field || column.key
      return this.getNestedValue(row, field)
    },
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => current?.[key], obj)
    },
    handleSort(field) {
      if (this.sort.by === field) {
        this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.by = field
        // Find column definition to get default sort order
        const column = this.columns.find(col => (col.field || col.key) === field)
        this.sort.order = column && column.defaultSortOrder ? column.defaultSortOrder : 'asc'
      }

      this.pagination.currentPage = 1

      if (this.serverSide) {
        this.$emit('sort', {
          by: this.sort.by,
          order: this.sort.order
        })
      }
    },
    updatePagination() {
      if (this.serverSide) {
        this.pagination.totalItems = this.totalItems
        this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.itemsPerPage) || 1
      } else {
        this.pagination.totalItems = this.sortedData.length
        this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.itemsPerPage) || 1
      }

      if (this.pagination.currentPage > this.pagination.totalPages) {
        this.pagination.currentPage = this.pagination.totalPages
      }

      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.pagination.totalItems)

      this.pagination.startItem = this.pagination.totalItems > 0 ? start : 0
      this.pagination.endItem = this.pagination.totalItems > 0 ? end : 0
    },
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page

        if (this.serverSide) {
          this.$emit('page-change', page)
        }
      }
    },
    handlePageSizeChange() {
      this.pagination.currentPage = 1

      if (this.serverSide) {
        this.$emit('page-size-change', this.pagination.itemsPerPage)
      } else {
        this.updatePagination()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.data-table-wrapper.is-light {
  --bg-primary: #ffffff;
  --bg-secondary: #f9fafb;
  --bg-hover: #f3f4f6;
  --bg-striped: #f9fafb;
  --border-color: #e5e7eb;
  --text-primary: #111827;
  --text-secondary: #6b7280;
  --input-bg: #ffffff;
  --input-border: #d1d5db;
  --input-focus: #3b82f6;
  --button-bg: #ffffff;
  --button-border: #d1d5db;
  --button-hover: #f3f4f6;
  --button-active: #3b82f6;
  --button-active-text: #ffffff;
  --button-disabled: #f3f4f6;
  --button-disabled-text: #9ca3af;
}

.data-table-wrapper.is-dark {
  --bg-primary: #1f2128;
  --bg-secondary: transparent;
  --bg-hover: #2a2d35;
  --bg-striped: #252830;
  --border-color: #3a3d45;
  --text-primary: #dddddf;
  --text-secondary: #9ca3af;
  --input-bg: #2a2d35;
  --input-border: #3a3d45;
  --input-focus: #3273dc;
  --button-bg: #2a2d35;
  --button-border: #3a3d45;
  --button-hover: #353840;
  --button-active: #3273dc;
  --button-active-text: #ffffff;
  --button-disabled: #1f2128;
  --button-disabled-text: #6b7280;
}

.data-table-wrapper {
  width: 100%;
  background: var(--bg-primary);
  border-radius: 8px;
  overflow: hidden;
}

.data-table-search {
  padding: 11px;
  border-bottom: 1px solid var(--border-color);

  .search-input {
    width: 100%;
    padding: 6px 8px;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: 6px;
    color: var(--text-primary);
    font-size: 14px;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--input-focus);
    }

    &.has-content {
      border-color: var(--primary-color, #1890ff);
      border-width: 2px;
      background: rgba(24, 144, 255, 0.05);
      box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
    }

    &::placeholder {
      color: var(--text-secondary);
    }
  }
}

.data-table-container {
  position: relative;
  overflow-x: auto;

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid var(--border-color);
      border-top-color: var(--primary-color, #3273dc);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  }
}

.is-dark .loading-overlay {
  background: rgba(0, 0, 0, 0.5);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: opacity 0.2s;
  table-layout: fixed;

  &.is-loading {
    opacity: 0.5;
  }

  thead {
    background: var(--bg-secondary);
    border-bottom: 2px solid var(--border-color);
  }

  th {
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
    color: var(--text-primary);
    border-bottom: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
    user-select: none;

    &:last-child {
      border-right: none;
    }

    &.is-sortable {
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background: var(--bg-hover);
      }
    }
  }

  tbody tr {
    border-bottom: 1px solid var(--border-color);
    transition: background-color 0.2s;
  }

  &.is-striped tbody tr:nth-child(even) {
    background: var(--bg-striped);
  }

  &.is-hoverable tbody tr:hover {
    background: var(--bg-hover);
  }

  td {
    padding: 12px 16px;
    font-size: 14px;
    color: var(--text-primary);
    border-right: 1px solid var(--border-color);

    &:last-child {
      border-right: none;
    }

    &.align-left {
      text-align: left;
    }

    &.align-center {
      text-align: center;
    }

    &.align-right {
      text-align: right;
    }

    strong {
      font-weight: 600;
      color: var(--primary-color, #1890ff);
      background-color: rgba(24, 144, 255, 0.1);
      padding: 0 2px;
      border-radius: 2px;
    }
  }
}

.truncate-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 8px;

  &.align-left {
    justify-content: flex-start;
  }

  &.align-center {
    justify-content: center;
  }

  &.align-right {
    justify-content: flex-end;
  }

  .sort-icon {
    font-size: 12px;
    color: var(--text-primary);
  }

  .sort-icon-inactive {
    color: var(--text-secondary);
    opacity: 0.5;
  }
}

.empty-state {
  text-align: center;
  padding: 48px 16px !important;
  color: var(--text-secondary);
  font-size: 14px;
}

.data-table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 16px;

  .footer-left {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
  }

  .footer-right {
    display: flex;
    align-items: center;
  }
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 8px;

  .label {
    font-size: 14px;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  .page-size-select {
    padding: 6px 32px 6px 12px;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: 6px;
    color: var(--text-primary);
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: var(--input-focus);
    }
  }
}

.showing-info {
  font-size: 14px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;

  .pagination-btn,
  .page-btn {
    padding: 6px 12px;
    background: var(--button-bg);
    border: 1px solid var(--button-border);
    border-radius: 6px;
    color: var(--text-primary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .pagination-btn {
    &:hover:not(:disabled) {
      background: var(--button-hover);
    }

    &:disabled {
      background: var(--button-disabled);
      color: var(--button-disabled-text);
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .page-btn {
    &:hover:not(.active) {
      background: var(--button-hover);
    }

    &.active {
      background: var(--button-active);
      color: var(--button-active-text);
      border-color: var(--button-active);
    }
  }

  .page-numbers {
    display: flex;
    gap: 4px;
  }
}

@media (max-width: 768px) {
  .data-table-footer {
    flex-direction: column;
    align-items: stretch;

    .footer-left,
    .footer-right {
      width: 100%;
      justify-content: center;
    }
  }

  .pagination-controls {
    justify-content: center;
  }

  .data-table-search .search-input {
    max-width: 100%;
  }
}

</style>

